/**
 * EltJobExecutionTable Component
 *
 * Table displaying job executions.
 */

import React from "react";
import { Button } from "../../../components/ui/Button";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import { EltJobExecutionStatusBadge } from "./EltJobExecutionStatusBadge";
import { TriggerBadge } from "./TriggerBadge";
import type { EltJobExecution, ExtractionStatus, LoadStatus, TransformStatus } from "../../../types/elt-job-execution.types";
import { EyeIcon, XMarkIcon } from "@heroicons/react/24/outline";

type PhaseStatus = ExtractionStatus | LoadStatus | TransformStatus;

// Compact phase status badge for table cells
const phaseStatusConfig: Record<string, { label: string; className: string; pulse: boolean }> = {
  pending: { label: "Pending", className: "bg-gray-100 text-gray-600", pulse: false },
  succeeded: { label: "Succeeded", className: "bg-green-100 text-green-800", pulse: false },
  failed: { label: "Failed", className: "bg-red-100 text-red-800", pulse: false },
  skipped: { label: "Skipped", className: "bg-gray-100 text-gray-500", pulse: false },
  running: { label: "Running", className: "bg-blue-100 text-blue-800", pulse: true },
  downloading: { label: "Downloading", className: "bg-blue-100 text-blue-800", pulse: true },
  creating_tables: { label: "Creating", className: "bg-blue-100 text-blue-800", pulse: true },
  cleaning: { label: "Cleaning", className: "bg-blue-100 text-blue-800", pulse: true },
  loading: { label: "Loading", className: "bg-blue-100 text-blue-800", pulse: true },
  staging: { label: "Staging", className: "bg-purple-100 text-purple-800", pulse: true },
  production: { label: "Production", className: "bg-purple-100 text-purple-800", pulse: true },
};

const PhaseStatusCell: React.FC<{ status?: PhaseStatus }> = ({ status }) => {
  if (!status) return <span className="text-gray-400">-</span>;
  const config = phaseStatusConfig[status] || phaseStatusConfig.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.pulse && (
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current" />
        </span>
      )}
      {config.label}
    </span>
  );
};

interface EltJobExecutionTableProps {
  executions: EltJobExecution[];
  isLoading: boolean;
  onView: (execution: EltJobExecution) => void;
  onCancel?: (execution: EltJobExecution) => void;
}

export const EltJobExecutionTable: React.FC<EltJobExecutionTableProps> = ({ executions, isLoading, onView, onCancel }) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={10} />;
  }

  if (executions.length === 0) {
    return (
      <EmptyState
        title="No job executions found"
        description="No job executions found."
      />
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (execution: EltJobExecution): string => {
    if (!execution.startedAt) return "-";
    const start = new Date(execution.startedAt).getTime();
    const end = execution.completedAt ? new Date(execution.completedAt).getTime() : Date.now();
    const seconds = (end - start) / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Execution</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extraction</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Load</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transform</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trigger</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Queries</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {executions.map((execution) => {
            const successfulQueries = execution.queryResults?.filter((q) => q.status === "succeeded").length || 0;
            const totalQueries = execution.queryResults?.length || 0;

            return (
              <tr key={execution.jobExecutionId} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-mono text-gray-900">{execution.jobExecutionCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <EltJobExecutionStatusBadge status={execution.status} />
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <PhaseStatusCell status={execution.extractionStatus} />
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <PhaseStatusCell status={execution.loadStatus} />
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <PhaseStatusCell status={execution.transformStatus} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <TriggerBadge trigger={execution.triggeredBy} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(execution.startedAt || execution.triggeredAt)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDuration(execution)}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {totalQueries > 0 ? (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        successfulQueries === totalQueries
                          ? "bg-green-100 text-green-800"
                          : successfulQueries > 0
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {successfulQueries} / {totalQueries}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={() => onView(execution)} title="View Details">
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                    {(execution.status === "pending" || execution.status === "in_progress") && onCancel && (
                      <Button variant="secondary" size="sm" onClick={() => onCancel(execution)} title="Cancel Execution">
                        <XMarkIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
